import React, { useEffect, useRef, useState } from 'react';
import styles from './Floatingassessmentwidget.module.css';

const ASSESSMENT_URL = 'https://cloudisian.net/ai-assesment';
const ATTENTION_INTERVAL_MS = 7000;   // fires more often — this widget wants to be noticed
const ATTENTION_VISIBLE_MS = 3600;
const DISMISS_KEY = 'onyxx_widget_dismissed';

export default function OnyxxAssessmentWidget() {
  const [dismissed, setDismissed] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [pulsing, setPulsing] = useState(false);
  const isHovered = useRef(false);

  useEffect(() => {
    if (sessionStorage.getItem(DISMISS_KEY) !== 'true') {
      setDismissed(false);
    }
  }, []);

  useEffect(() => {
    if (dismissed) return;

    const interval = setInterval(() => {
      if (isHovered.current) return;

      setExpanded(true);
      setPulsing(true);

      const pulseTimeout = setTimeout(() => setPulsing(false), 1300);
      const collapseTimeout = setTimeout(() => setExpanded(false), ATTENTION_VISIBLE_MS);

      return () => {
        clearTimeout(pulseTimeout);
        clearTimeout(collapseTimeout);
      };
    }, ATTENTION_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [dismissed]);

  if (dismissed) return null;

  const handleDismiss = (e) => {
    e.stopPropagation();
    sessionStorage.setItem(DISMISS_KEY, 'true');
    setDismissed(true);
  };

  const handleClick = () => {
    window.location.href = ASSESSMENT_URL;
  };

  return (
    <div
      className={`${styles.container} ${expanded ? styles.expanded : ''} ${pulsing ? styles.attentionPulse : ''}`}
      onMouseEnter={() => {
        isHovered.current = true;
        setExpanded(true);
      }}
      onMouseLeave={() => {
        isHovered.current = false;
        setExpanded(false);
      }}
    >
      <button
        type="button"
        className={styles.mainAction}
        onClick={handleClick}
        aria-label="Assess your company's AI maturity with Onyxx"
      >
        {/* Ambient brand glow, always on */}
        <div className={styles.halo} aria-hidden="true" />

        {/* Beacon rings, fire on the attention nudge */}
        <div className={styles.beacon} aria-hidden="true" />
        <div className={styles.beacon} aria-hidden="true" />

        {/* Shield badge */}
        <div className={styles.badge}>
          <svg
            className={styles.icon}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              className={styles.shieldOutline}
              d="M12 2.5L4 5.5V11C4 16 7.4 20.2 12 21.5C16.6 20.2 20 16 20 11V5.5L12 2.5Z"
              strokeLinejoin="round"
            />
            <path
              className={styles.checkPath}
              d="M8.5 12.2L11 14.7L15.7 9.5"
            />
          </svg>

          {/* Notification dot — the visual cue people can't help but check */}
          <span className={styles.notifDot} aria-hidden="true" />
        </div>

        {/* Two-line pill with brand name + social proof */}
        <div className={styles.pill}>
          <span className={styles.pillShine} aria-hidden="true" />
          <span className={styles.pillTitle}>
            Discover Your AI Landscape
            <span className={styles.pillArrow}>
              <svg width="11" height="11" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2 6H10M10 6L6.5 2.5M10 6L6.5 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </span>
          <span className={styles.pillSubtitle}>See how AI is used across your company.</span>
        </div>
      </button>

      {/* Sleek Minimal Close Button */}
      <button
        type="button"
        className={styles.closeBtn}
        onClick={handleDismiss}
        aria-label="Dismiss Onyxx AI maturity assessment widget"
      >
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 1L9 9M9 1L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}
